import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Settings, GripVertical, Trash2, Edit3, X, Check, Filter, Bell } from 'lucide-react';
import { TasksBoard, BoardWithColumns, BoardColumn, TaskCard } from '../types/board';
import { getBoardWithColumns, createColumn, createCard, updateColumnPositions, updateCardPositions, updateColumnTitle, deleteColumn, updateCard } from '../lib/kanban';
import { updateBoardStatusFilter } from '../lib/boards';
import { useTaskCardsRealtime } from '../hooks/useTaskCardsRealtime';
import { toggleColumnNotification, isColumnNotificationEnabled } from '../lib/taskNotifications';
import { useAuth } from '../hooks/useAuth';
import { useBusiness } from '../contexts/BusinessContext';

interface KanbanBoardProps {
  board: TasksBoard;
  boardData?: BoardWithColumns;
  onClose: () => void;
  onOpenBookingInfo?: (booking: any) => void;
}

export const KanbanBoard: React.FC<KanbanBoardProps> = ({ board, boardData: preloadedBoardData, onClose, onOpenBookingInfo }) => {
  const { user } = useAuth();
  const { selectedBusiness } = useBusiness();
  const navigate = useNavigate();
  
  const [boardData, setBoardData] = useState<BoardWithColumns | null>(preloadedBoardData || null);
  const [isLoading, setIsLoading] = useState(!preloadedBoardData);
  const [isAddingColumn, setIsAddingColumn] = useState(false);
  const [newColumnTitle, setNewColumnTitle] = useState('');
  const [addingCardToColumn, setAddingCardToColumn] = useState<string | null>(null);
  const [newCardTitle, setNewCardTitle] = useState('');
  const [newCardDescription, setNewCardDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);
  const [draggedCard, setDraggedCard] = useState<string | null>(null);
  const [dragOverCard, setDragOverCard] = useState<string | null>(null);
  const [editingColumn, setEditingColumn] = useState<string | null>(null);
  const [editColumnTitle, setEditColumnTitle] = useState('');
  const [showColumnSettings, setShowColumnSettings] = useState<string | null>(null);
  const [showCardSettings, setShowCardSettings] = useState<string | null>(null);
  const [editCardTitle, setEditCardTitle] = useState('');
  const [editCardDescription, setEditCardDescription] = useState('');
  const [editCardStatus, setEditCardStatus] = useState<TaskCard['status']>('in-progress');
  const [editCardUrgency, setEditCardUrgency] = useState<TaskCard['urgency']>('medium');
  const [editCardScheduledDate, setEditCardScheduledDate] = useState('');
  
  // Filter state
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [statusFilters, setStatusFilters] = useState<Record<TaskCard['status'], boolean>>(board.status_filter);

  // Real-time connection status
  const [realtimeConnected, setRealtimeConnected] = useState(false);

  // Notification state
  const [notificationEnabled, setNotificationEnabled] = useState<boolean>(false);
  const [isCheckingNotification, setIsCheckingNotification] = useState<boolean>(false);
  const [isUpdatingNotification, setIsUpdatingNotification] = useState<boolean>(false);
  const [columnNotifications, setColumnNotifications] = useState<Record<string, boolean>>({});

  // Memoize callback functions to prevent unnecessary re-renders
  const handleRealtimeError = useCallback((error: string) => {
    console.error('🚨 Real-time error:', error);
    setError(error);
  }, []);

  const handleConnectionChange = useCallback((connected: boolean) => {
    console.log('🔗 Connection status changed:', connected);
    setRealtimeConnected(connected);
  }, []);

  // Set up real-time subscription for task cards
  useTaskCardsRealtime({
    boardId: board.id,
    boardData,
    setBoardData,
    onError: handleRealtimeError,
    onConnectionChange: handleConnectionChange
  });

  // Load board data when component mounts or board changes
  useEffect(() => {
    // Only load if we don't have pre-loaded data
    if (!preloadedBoardData) {
      loadBoardData();
    }
  }, [board.id, preloadedBoardData]);

  // Update status filters when board changes
  useEffect(() => {
    setStatusFilters(board.status_filter);
  }, [board.status_filter]);

  // Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showFilterDropdown && !event.target) return;
      const target = event.target as HTMLElement;
      if (target && !target.closest('.filter-dropdown')) {
        setShowFilterDropdown(false);
      }
    };

    if (showFilterDropdown) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showFilterDropdown]);

  const loadBoardData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await getBoardWithColumns(board.id);
      setBoardData(data);
    } catch (err) {
      console.error('Error loading board data:', err);
      setError('Failed to load board data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddColumn = async () => {
    if (!newColumnTitle.trim() || !boardData) return;

    try {
      await createColumn(board.id, board.business_id, {
        title: newColumnTitle.trim()
      });
      
      // Reload board data to show the new column
      await loadBoardData();
      
      // Reset form
      setNewColumnTitle('');
      setIsAddingColumn(false);
    } catch (err) {
      console.error('Error creating column:', err);
      setError('Failed to create column');
    }
  };

  const handleCancelAddColumn = () => {
    setNewColumnTitle('');
    setIsAddingColumn(false);
  };

  const handleAddCard = async (columnId: string) => {
    if (!newCardTitle.trim() || !boardData) return;

    console.log('🎯 Creating new card:', {
      columnId,
      boardId: board.id,
      businessId: board.business_id,
      title: newCardTitle.trim(),
      description: newCardDescription.trim() || undefined
    });

    try {
      const newCard = await createCard(columnId, board.id, board.business_id, {
        title: newCardTitle.trim(),
        description: newCardDescription.trim() || undefined,
        status: 'in-progress', // Default status
        urgency: 'medium' // Default urgency
      });
      
      console.log('✅ Card created successfully:', newCard);
      console.log('⏳ Waiting for real-time subscription to update UI...');
      
      // Real-time subscription will handle the UI update automatically
      
      // Reset form
      setNewCardTitle('');
      setNewCardDescription('');
      setAddingCardToColumn(null);
    } catch (err) {
      console.error('❌ Error creating card:', err);
      setError('Failed to create card');
    }
  };

  const handleCancelAddCard = () => {
    setNewCardTitle('');
    setNewCardDescription('');
    setAddingCardToColumn(null);
  };

  // Drag and drop handlers for column reordering
  const handleDragStart = (e: React.DragEvent, columnId: string) => {
    setDraggedColumn(columnId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', columnId);
  };

  const handleDragEnd = () => {
    setDraggedColumn(null);
    setDragOverColumn(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e: React.DragEvent, columnId: string) => {
    e.preventDefault();
    setDragOverColumn(columnId);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    // Only remove drag over if we're leaving the column completely
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragOverColumn(null);
    }
  };

  const handleDrop = async (e: React.DragEvent, targetColumnId: string) => {
    e.preventDefault();
    setDragOverColumn(null);

    if (!draggedColumn || !boardData || draggedColumn === targetColumnId) {
      setDraggedColumn(null);
      return;
    }

    try {
      // Find the dragged column and target column
      const draggedIndex = boardData.columns.findIndex(col => col.id === draggedColumn);
      const targetIndex = boardData.columns.findIndex(col => col.id === targetColumnId);
      
      if (draggedIndex === -1 || targetIndex === -1) {
        return;
      }

      // Create a new array with reordered columns
      const newColumns = [...boardData.columns];
      const [draggedItem] = newColumns.splice(draggedIndex, 1);
      newColumns.splice(targetIndex, 0, draggedItem);

      // Update local state immediately for better UX
      setBoardData({
        ...boardData,
        columns: newColumns
      });

      // Prepare position updates
      const positionUpdates = newColumns.map((column, index) => ({
        id: column.id,
        position: index
      }));

      // Update positions in the database
      await updateColumnPositions(board.id, positionUpdates);

    } catch (err) {
      console.error('Error reordering columns:', err);
      setError('Failed to reorder columns');
      // Reload board data to revert changes
      await loadBoardData();
    } finally {
      setDraggedColumn(null);
    }
  };

  // Drag and drop handlers for card reordering/moving
  const handleCardDragStart = (e: React.DragEvent, cardId: string) => {
    setDraggedCard(cardId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', cardId);
    e.stopPropagation(); // Prevent column drag
  };

  const handleCardDragEnd = () => {
    setDraggedCard(null);
    setDragOverCard(null);
  };

  const handleCardDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleCardDragEnter = (e: React.DragEvent, cardId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverCard(cardId);
  };

  const handleCardDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only remove drag over if we're leaving the card completely
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragOverCard(null);
    }
  };

  const handleCardDrop = async (e: React.DragEvent, targetCardId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverCard(null);

    if (!draggedCard || !boardData || draggedCard === targetCardId) {
      setDraggedCard(null);
      return;
    }

    try {
      // Find the dragged card and target card
      let draggedCardData: TaskCard | null = null;
      let targetCardData: TaskCard | null = null;
      let sourceColumnId: string | null = null;
      let targetColumnId: string | null = null;

      // Find cards and their columns
      for (const column of boardData.columns) {
        for (const card of column.cards) {
          if (card.id === draggedCard) {
            draggedCardData = card;
            sourceColumnId = column.id;
          }
          if (card.id === targetCardId) {
            targetCardData = card;
            targetColumnId = column.id;
          }
        }
      }

      if (!draggedCardData || !targetCardData || !sourceColumnId || !targetColumnId) {
        return;
      }

      // Create new board structure
      const newColumns = boardData.columns.map(column => ({
        ...column,
        cards: [...column.cards]
      }));

      // Remove card from source column
      const sourceColumn = newColumns.find(col => col.id === sourceColumnId);
      if (sourceColumn) {
        sourceColumn.cards = sourceColumn.cards.filter(card => card.id !== draggedCard);
      }

      // Add card to target column at the right position
      const targetColumn = newColumns.find(col => col.id === targetColumnId);
      if (targetColumn) {
        const targetIndex = targetColumn.cards.findIndex(card => card.id === targetCardId);
        targetColumn.cards.splice(targetIndex, 0, {
          ...draggedCardData,
          column_id: targetColumnId
        });
      }

      // Update local state immediately for instant UI response
      setBoardData({
        ...boardData,
        columns: newColumns
      });

      console.log('🚀 OPTIMIZED: Updating only the moved card in database...');

      // Only update the moved card in the database (much faster!)
      if (targetColumn) {
        const targetIndex = targetColumn.cards.findIndex(card => card.id === draggedCard);
        await updateCard(draggedCard, {
          column_id: targetColumnId,
          position: targetIndex
          });
      }

      console.log('✅ OPTIMIZED: Single card update completed!');

      // Real-time subscription will handle updating other users' views
      // No need to update ALL cards - that was the performance bottleneck!

    } catch (err) {
      console.error('Error moving card:', err);
      setError('Failed to move card');
      // Reload board data to revert changes
      await loadBoardData();
    } finally {
      setDraggedCard(null);
    }
  };

  // Handle dropping a card on a column (for empty columns or at the end)
  const handleColumnCardDrop = async (e: React.DragEvent, columnId: string) => {
    e.preventDefault();
    e.stopPropagation();

    if (!draggedCard || !boardData) {
      return;
    }

    try {
      // Find the dragged card
      let draggedCardData: TaskCard | null = null;
      let sourceColumnId: string | null = null;

      for (const column of boardData.columns) {
        for (const card of column.cards) {
          if (card.id === draggedCard) {
            draggedCardData = card;
            sourceColumnId = column.id;
            break;
          }
        }
      }

      if (!draggedCardData || !sourceColumnId || sourceColumnId === columnId) {
        setDraggedCard(null);
        return;
      }

      // Create new board structure
      const newColumns = boardData.columns.map(column => ({
        ...column,
        cards: [...column.cards]
      }));

      // Remove card from source column
      const sourceColumn = newColumns.find(col => col.id === sourceColumnId);
      if (sourceColumn) {
        sourceColumn.cards = sourceColumn.cards.filter(card => card.id !== draggedCard);
      }

      // Add card to target column at the end
      const targetColumn = newColumns.find(col => col.id === columnId);
      if (targetColumn) {
        targetColumn.cards.push({
          ...draggedCardData,
          column_id: columnId
        });
      }

      // Update local state immediately for instant UI response
      setBoardData({
        ...boardData,
        columns: newColumns
      });

      console.log('🚀 OPTIMIZED: Updating only the moved card in database (column drop)...');

      // Only update the moved card in the database (much faster!)
      if (targetColumn) {
        const newPosition = targetColumn.cards.length - 1; // Added to end
        await updateCard(draggedCard, {
          column_id: columnId,
          position: newPosition
          });
      }

      console.log('✅ OPTIMIZED: Single card update completed (column drop)!');

      // Real-time subscription will handle updating other users' views

    } catch (err) {
      console.error('Error moving card to column:', err);
      setError('Failed to move card');
      // Reload board data to revert changes
      await loadBoardData();
    } finally {
      setDraggedCard(null);
    }
  };

  // Column settings handlers
  const handleUpdateColumnTitle = async (columnId: string, newTitle: string) => {
    if (!newTitle.trim() || !boardData) return;

    try {
      await updateColumnTitle(columnId, newTitle.trim());
      
      // Update local state
      setBoardData({
        ...boardData,
        columns: boardData.columns.map(col => 
          col.id === columnId ? { ...col, title: newTitle.trim() } : col
        )
      });
      
      setEditingColumn(null);
      setEditColumnTitle('');
    } catch (err) {
      console.error('Error updating column title:', err);
      setError('Failed to update column title');
    }
  };

  const handleDeleteColumn = async (columnId: string) => {
    if (!boardData) return;

    // Show confirmation
    if (!window.confirm('Are you sure you want to delete this list? This will also delete all cards in this list.')) {
      return;
    }

    try {
      await deleteColumn(columnId);
      
      // Update local state
      setBoardData({
        ...boardData,
        columns: boardData.columns.filter(col => col.id !== columnId)
      });
      
      setShowColumnSettings(null);
    } catch (err) {
      console.error('Error deleting column:', err);
      setError('Failed to delete column');
    }
  };

  const handleOpenColumnSettings = (columnId: string) => {
    setShowColumnSettings(columnId);
  };

  const handleCloseColumnSettings = () => {
    setShowColumnSettings(null);
    setEditingColumn(null);
    setEditColumnTitle('');
  };

  const handleStartEditingColumn = (columnId: string, currentTitle: string) => {
    setEditingColumn(columnId);
    setEditColumnTitle(currentTitle);
  };

  // Notification handlers
  const checkNotificationStatus = async (columnId: string) => {
    if (!user?.id) return;
    
    setIsCheckingNotification(true);
    try {
      const enabled = await isColumnNotificationEnabled(user.id, columnId);
      setNotificationEnabled(enabled);
    } catch (error) {
      console.error('Error checking notification status:', error);
    } finally {
      setIsCheckingNotification(false);
    }
  };

  // Load notification status for all columns in the board
  const loadAllColumnNotifications = async () => {
    if (!user?.id || !boardData?.columns) return;
    
    try {
      const notifications: Record<string, boolean> = {};
      
      // Check notification status for each column
      await Promise.all(
        boardData.columns.map(async (column) => {
          const enabled = await isColumnNotificationEnabled(user.id, column.id);
          notifications[column.id] = enabled;
        })
      );
      
      setColumnNotifications(notifications);
    } catch (error) {
      console.error('Error loading column notifications:', error);
    }
  };

  const handleToggleColumnNotification = async (columnId: string) => {
    if (!user?.id || !selectedBusiness?.id) return;
    
    setIsUpdatingNotification(true);
    try {
      const currentEnabled = columnNotifications[columnId] || false;
      const newEnabled = !currentEnabled;
      
      await toggleColumnNotification(user.id, columnId, selectedBusiness.id, newEnabled);
      
      // Update the column notifications state to reflect the change immediately
      setColumnNotifications(prev => ({
        ...prev,
        [columnId]: newEnabled
      }));
      
      // Also update the single notification state if this is the currently opened column
      if (showColumnSettings === columnId) {
        setNotificationEnabled(newEnabled);
      }
      
    } catch (error) {
      console.error('Error toggling notification:', error);
      setError('Failed to update notification preference');
      
      // Revert the change on error
      setColumnNotifications(prev => ({
        ...prev,
        [columnId]: !prev[columnId]
      }));
    } finally {
      setIsUpdatingNotification(false);
    }
  };

  // Check notification status when column settings modal opens
  useEffect(() => {
    if (showColumnSettings && user?.id) {
      checkNotificationStatus(showColumnSettings);
    }
  }, [showColumnSettings, user?.id]);

  // Load notification status for all columns when board data is loaded
  useEffect(() => {
    if (boardData && user?.id) {
      loadAllColumnNotifications();
    }
  }, [boardData, user?.id]);

  // Card settings handlers
  const handleOpenCardSettings = (card: TaskCard) => {
    setShowCardSettings(card.id);
    setEditCardTitle(card.title);
    setEditCardDescription(card.description || '');
    setEditCardStatus(card.status);
    setEditCardUrgency(card.urgency);
    // Convert UTC stored time to local time for datetime-local input
    if (card.scheduled_date) {
      const utcDate = new Date(card.scheduled_date);
      // Adjust for timezone offset to get local time
      const localDate = new Date(utcDate.getTime() - (utcDate.getTimezoneOffset() * 60000));
      setEditCardScheduledDate(localDate.toISOString().slice(0, 16));
    } else {
      setEditCardScheduledDate('');
    }
  };

  const handleCloseCardSettings = () => {
    setShowCardSettings(null);
    setEditCardTitle('');
    setEditCardDescription('');
    setEditCardStatus('in-progress');
    setEditCardUrgency('medium');
    setEditCardScheduledDate('');
  };

  // Handle scheduled date change - auto-update status
  const handleScheduledDateChange = (dateValue: string) => {
    setEditCardScheduledDate(dateValue);
    // If a date is set, automatically change status to "scheduled"
    if (dateValue) {
      setEditCardStatus('scheduled');
    }
    // If date is cleared and status was "scheduled", change to "in-progress"
    else if (editCardStatus === 'scheduled') {
      setEditCardStatus('in-progress');
    }
  };

  const handleUpdateCard = async (cardId: string) => {
    if (!editCardTitle.trim() || !boardData) return;

      const updates = {
        title: editCardTitle.trim(),
        description: editCardDescription.trim() || undefined,
        status: editCardStatus,
        urgency: editCardUrgency,
        scheduled_date: editCardScheduledDate ? new Date(editCardScheduledDate).toISOString() : undefined
      };

    console.log('🎯 Updating card:', { cardId, updates });

    try {
      const updatedCard = await updateCard(cardId, updates);
      
      console.log('✅ Card updated successfully:', updatedCard);
      console.log('⏳ Waiting for real-time subscription to update UI...');
      
      // Real-time subscription will handle the UI update automatically
      
      handleCloseCardSettings();
    } catch (err) {
      console.error('❌ Error updating card:', err);
      setError('Failed to update card');
    }
  };

  const handleMarkAsCompleted = async (cardId: string) => {
    if (!boardData) return;

    console.log('🎯 Marking card as completed:', cardId);

    try {
      const updatedCard = await updateCard(cardId, {
        status: 'completed'
      });
      
      console.log('✅ Card marked as completed successfully:', updatedCard);
      console.log('⏳ Waiting for real-time subscription to update UI...');
      
      // Real-time subscription will handle the UI update automatically
    } catch (err) {
      console.error('❌ Error marking card as completed:', err);
      setError('Failed to mark card as completed');
    }
  };

  const handleToggleStatusFilter = async (status: TaskCard['status']) => {
    const newFilters = {
      ...statusFilters,
      [status]: !statusFilters[status]
    };
    
    setStatusFilters(newFilters);
    
    // Update the database with the new filter settings
    try {
      await updateBoardStatusFilter(board.id, newFilters);
    } catch (error) {
      console.error('Error updating board status filter:', error);
      // Revert the local state if the database update fails
      setStatusFilters(statusFilters);
    }
  };

  const getFilteredCards = (cards: TaskCard[]) => {
    return cards.filter(card => statusFilters[card.status]);
  };

  const getUrgencyColor = (urgency: TaskCard['urgency']) => {
    switch (urgency) {
      case 'low':
        return 'bg-green-100 text-green-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'high':
        return 'bg-orange-100 text-orange-800';
      case 'alert':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getUrgencyText = (urgency: TaskCard['urgency']) => {
    switch (urgency) {
      case 'low':
        return 'Low';
      case 'medium':
        return 'Medium';
      case 'high':
        return 'High';
      case 'alert':
        return 'Alert';
      default:
        return urgency;
    }
  };

  const getStatusColor = (status: TaskCard['status']) => {
    switch (status) {
      case 'scheduled':
        return 'bg-blue-100 text-blue-800';
      case 'in-progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: TaskCard['status']) => {
    switch (status) {
      case 'scheduled':
        return 'Scheduled';
      case 'in-progress':
        return 'In Progress';
      case 'completed':
        return 'Completed';
      default:
        return status;
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col h-full bg-gray-100" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
        <div className="flex items-center justify-between p-4 bg-white border-b">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">{board.board_title}</h1>
            <p className="text-sm text-gray-500">Loading...</p>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-gray-500">Loading board data...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col h-full bg-gray-100" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
        <div className="flex items-center justify-between p-4 bg-white border-b">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">{board.board_title}</h1>
            <p className="text-sm text-red-500">{error}</p>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={loadBoardData}
                              className="px-4 py-2 bg-purple-700 text-white rounded-xl hover:bg-purple-800 transition-all duration-200 active:scale-95"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!boardData) {
    return null;
  }

  return (
    <div className="flex flex-col h-full bg-gray-100" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
      {/* Board Header */}
      <div className="flex items-center justify-between p-4 bg-white border-b">
        <div className="flex items-center space-x-4">
          <div>
              <h1 className="text-xl font-semibold text-gray-900">{boardData.board_title}</h1>
            <p className="text-sm text-gray-500">
              {boardData.assigned_users.length} member{boardData.assigned_users.length !== 1 ? 's' : ''} • {boardData.columns.length} list{boardData.columns.length !== 1 ? 's' : ''}
            </p>
          </div>
          
          {/* Filter Button */}
          <div className="relative filter-dropdown">
            <button
              onClick={() => setShowFilterDropdown(!showFilterDropdown)}
              className="flex items-center space-x-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
            >
              <Filter size={16} />
              <span className="text-sm font-medium">Filter</span>
            </button>
            
            {/* Filter Dropdown */}
            {showFilterDropdown && (
              <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 py-2 z-10">
                <div className="px-3 py-2 text-sm font-medium text-gray-700 border-b border-gray-200">
                  Filter by Status
                </div>
                {Object.entries(statusFilters).map(([status, checked]) => (
                  <div
                    key={status}
                    className="px-3 py-2 hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleToggleStatusFilter(status as TaskCard['status'])}
                  >
                    <div className="flex items-center space-x-2">
                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                        checked ? 'bg-blue-500 border-blue-500' : 'border-gray-300'
                      }`}>
                        {checked && <Check size={10} className="text-white" />}
                      </div>
                      <span className="text-sm">{getStatusText(status as TaskCard['status'])}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Board Content */}
      <div className="flex-1 overflow-x-auto overflow-y-hidden custom-scrollbar md:snap-none snap-x snap-mandatory">
        <div className="flex space-x-6 h-full px-8 py-6" style={{ minWidth: `${Math.max(1200, (boardData.columns.length + 1) * 336)}px` }}>
          {/* Existing Columns */}
          {boardData.columns.map((column) => (
            <div
              key={column.id}
              className={`w-[calc(100vw-6rem)] md:w-80 flex-shrink-0 bg-gray-50 rounded-2xl p-6 flex flex-col transition-all duration-200 snap-start shadow-lg border border-gray-200/50 ${
                draggedColumn === column.id ? 'opacity-50 scale-105' : ''
              } ${
                dragOverColumn === column.id ? 'ring-2 ring-purple-400 shadow-xl' : ''
              }`}
              onDragOver={(e) => {
                handleDragOver(e);
                // handleCardDragOver(e); // This is now handled by the card's onDragOver
              }}
              onDragEnter={(e) => handleDragEnter(e, column.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => {
                // Check if we're dropping a card or a column
                if (draggedCard) {
                  handleColumnCardDrop(e, column.id);
                } else {
                  handleDrop(e, column.id);
                }
              }}
            >
              {/* Column Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div 
                    className="cursor-grab active:cursor-grabbing text-gray-500 hover:text-gray-700 p-2 rounded-lg hover:bg-gray-100 transition-all duration-200 active:scale-95"
                    draggable
                    onDragStart={(e) => handleDragStart(e, column.id)}
                    onDragEnd={handleDragEnd}
                  >
                    <GripVertical size={18} />
                  </div>
                  <h3 className="font-bold text-gray-900 text-lg">{column.title}</h3>
                </div>
                <div className="flex items-center space-x-2">
                  {/* Notification Bell Button - Always visible */}
                  <button
                    className={`relative p-2 rounded-lg hover:bg-gray-100 transition-all duration-200 active:scale-95 ${
                      columnNotifications[column.id] 
                        ? 'text-purple-600' 
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                    onClick={() => handleToggleColumnNotification(column.id)}
                    title={
                      columnNotifications[column.id] 
                        ? 'Notifications enabled - click to disable' 
                        : 'Enable notifications for this list'
                    }
                    disabled={isUpdatingNotification}
                  >
                    <Bell size={16} />
                    {/* Checkmark overlay when notifications are enabled */}
                    {columnNotifications[column.id] && (
                      <div className="absolute -top-0.5 -right-0.5 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center">
                        <Check size={10} />
                      </div>
                    )}
                  </button>
                  
                  {/* Settings Button */}
                <button 
                  className="text-gray-500 hover:text-gray-700 p-2 rounded-lg hover:bg-gray-100 transition-all duration-200 active:scale-95"
                  onClick={() => handleOpenColumnSettings(column.id)}
                >
                  <Settings size={16} />
                </button>
                </div>
              </div>

              {/* Add Card Section - Moved to top */}
              <div className="mb-2">
                {addingCardToColumn === column.id ? (
                  /* Add Card Form */
                  <div className="bg-white rounded-lg p-3 shadow-sm border">
                    <div className="space-y-3">
                      <input
                        type="text"
                        placeholder="Enter a title for this card..."
                        value={newCardTitle}
                        onChange={(e) => setNewCardTitle(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleAddCard(column.id);
                          } else if (e.key === 'Escape') {
                            handleCancelAddCard();
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                        autoFocus
                      />
                      <textarea
                        placeholder="Enter a description (optional)..."
                        value={newCardDescription}
                        onChange={(e) => setNewCardDescription(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && e.ctrlKey) {
                            e.preventDefault();
                            handleAddCard(column.id);
                          } else if (e.key === 'Escape') {
                            handleCancelAddCard();
                          }
                        }}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm resize-none"
                      />
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleAddCard(column.id)}
                          disabled={!newCardTitle.trim()}
                          className="px-3 py-1 text-sm font-medium text-white bg-purple-700 hover:bg-purple-800 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 active:scale-95"
                        >
                          Add card
                        </button>
                        <button
                          onClick={handleCancelAddCard}
                          className="px-3 py-1 text-sm font-medium text-gray-600 hover:text-gray-800"
                        >
                          Cancel
                        </button>
                      </div>
                      <div className="text-xs text-gray-500">
                        Press Enter to add, Ctrl+Enter from description, or Escape to cancel
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Add Card Button */
                  <button 
                    onClick={() => setAddingCardToColumn(column.id)}
                    className="w-full p-2 text-left text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-lg transition-colors flex items-center"
                  >
                    <Plus size={16} className="mr-2" />
                    Add another card
                  </button>
                )}
              </div>

              {/* Cards */}
              <div className="space-y-4 flex-1 overflow-y-auto column-scroll">
                {getFilteredCards(column.cards).map((card) => (
                  <div
                    key={card.id}
                    className={`bg-white rounded-xl p-4 shadow-md border border-gray-200/50 hover:shadow-lg transition-all duration-200 active:scale-95 ${
                      draggedCard === card.id ? 'opacity-50 scale-105' : ''
                    } ${
                      dragOverCard === card.id ? 'ring-2 ring-purple-400 shadow-xl' : ''
                    }`}
                    onDragOver={handleCardDragOver}
                    onDragEnter={(e) => handleCardDragEnter(e, card.id)}
                    onDragLeave={handleCardDragLeave}
                    onDrop={(e) => handleCardDrop(e, card.id)}
                  >
                    {/* Card Header with Grip Handle */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-start space-x-3 flex-1">
                        <div
                          className="cursor-grab active:cursor-grabbing text-gray-500 hover:text-gray-700 p-1.5 rounded-lg hover:bg-gray-100 mt-0.5 transition-all duration-200 active:scale-95"
                          draggable
                          onDragStart={(e) => handleCardDragStart(e, card.id)}
                          onDragEnd={handleCardDragEnd}
                        >
                          <GripVertical size={14} />
                        </div>
                        <div className="flex-1">
                          {/* Card Status and Urgency Badges */}
                          <div className="mb-3 flex flex-wrap gap-2">
                            <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(card.status)}`}>
                              {getStatusText(card.status)}
                            </span>
                            <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getUrgencyColor(card.urgency)}`}>
                              {getUrgencyText(card.urgency)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <button
                        className="text-gray-500 hover:text-gray-700 p-1.5 rounded-lg hover:bg-gray-100 transition-all duration-200 active:scale-95"
                        onClick={() => handleOpenCardSettings(card)}
                      >
                        <Settings size={14} />
                      </button>
                    </div>

                    {/* Card Content */}
                    <div className="mb-4 ml-8">
                      <p className="text-base text-gray-900 font-semibold mb-2">{card.title}</p>
                      {card.description && (
                        <div className="text-xs text-gray-600">
                          {/* Check if description contains Customer Follow component */}
                          {card.description && card.description.includes('🔵 **Customer Follow-Up**') ? (
                            <div className="space-y-2">
                              {/* Regular description text before the Customer Follow block */}
                              {card.description && card.description.split('🔵 **Customer Follow-Up**')[0] && (
                                <p>{card.description.split('🔵 **Customer Follow-Up**')[0]}</p>
                              )}
                              
                              {/* Customer Follow block with clickable components */}
                              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                <div className="flex items-center mb-2">
                                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                                  <span className="text-sm font-semibold text-gray-800">Customer Follow-Up</span>
                                </div>
                                
                                {/* Extract customer info from the description */}
                                {(() => {
                                  const customerFollowMatch = card.description?.match(/Customer: ([^\n]+)/);
                                  const bookingMatch = card.description?.match(/Booking: ([^\n]+)/);
                                  const vehicleMatch = card.description?.match(/Vehicle: ([^\n]+)/);
                                  const serviceMatch = card.description?.match(/Service: ([^\n]+)/);
                                  const totalMatch = card.description?.match(/Total: ([^\n]+)/);
                                  
                                  return (
                                    <div className="space-y-1 text-xs mb-3">
                                      {customerFollowMatch && (
                                        <div className="flex justify-between">
                                          <span className="text-gray-600">Customer:</span>
                                          <button 
                                            className="text-blue-600 hover:text-blue-800 font-medium"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              const customerName = customerFollowMatch[1].trim();
                                              console.log('Opening customer details for:', customerName);
                                            }}
                                          >
                                            {customerFollowMatch[1].trim()}
                                          </button>
                                        </div>
                                      )}
                                      
                                      {bookingMatch && (
                                        <div className="flex justify-between">
                                          <span className="text-gray-600">Booking:</span>
                                          <button 
                                            className="text-blue-600 hover:text-blue-800 font-medium"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              const bookingNumber = bookingMatch[1].trim();
                                              console.log('Opening booking details for:', bookingNumber);
                                            }}
                                          >
                                            {bookingMatch[1].trim()}
                                          </button>
                                        </div>
                                      )}
                                      
                                      {vehicleMatch && (
                                        <div className="flex justify-between">
                                          <span className="text-gray-600">Vehicle:</span>
                                          <span className="text-gray-800">{vehicleMatch[1].trim()}</span>
                                        </div>
                                      )}
                                      
                                      {serviceMatch && (
                                        <div className="flex justify-between">
                                          <span className="text-gray-600">Service:</span>
                                          <span className="text-gray-800">{serviceMatch[1].trim()}</span>
                                        </div>
                                      )}
                                      
                                      {totalMatch && (
                                        <div className="flex justify-between">
                                          <span className="text-gray-600">Total:</span>
                                          <span className="text-gray-800 font-medium">${totalMatch[1].trim()}</span>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}
                                
                                {/* Action Button */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const customerMatch = card.description?.match(/Customer: ([^\n]+)/);
                                    if (customerMatch) {
                                      const customerName = customerMatch[1].trim();
                                      navigate(`/messages?customer=${encodeURIComponent(customerName)}`);
                                    }
                                  }}
                                  className="w-full bg-blue-600 text-white text-xs px-3 py-2 rounded hover:bg-blue-700 transition-colors"
                                >
                                  Message Customer
                                </button>
                              </div>
                            </div>
                          ) : (
                            <p>{card.description}</p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Card Footer */}
                    <div className="flex items-center justify-between ml-6">
                      <div className="flex items-center space-x-1">
                        {card.assigned_users.slice(0, 3).map((assignee, index) => (
                          <div
                            key={index}
                            className="w-6 h-6 bg-gray-300 rounded-full flex items-center justify-center text-xs font-medium text-gray-600"
                          >
                            {assignee.name.charAt(0).toUpperCase()}
                          </div>
                        ))}
                        {card.assigned_users.length > 3 && (
                          <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center text-xs font-medium text-gray-500">
                            +{card.assigned_users.length - 3}
                          </div>
                        )}
                      </div>
                      {/* Checkmark button */}
                      <button
                        onClick={() => handleMarkAsCompleted(card.id)}
                        className={`p-1 rounded-full hover:bg-green-100 transition-colors ${
                          card.status === 'completed' 
                            ? 'bg-green-500 text-white' 
                            : 'text-gray-400 hover:text-green-600'
                        }`}
                        title={card.status === 'completed' ? 'Task completed' : 'Mark as completed'}
                      >
                        <Check size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Add Column Section */}
          <div className="w-[calc(100vw-6rem)] md:w-80 flex-shrink-0 bg-gray-50 border-2 border-dashed border-gray-300 rounded-2xl p-6 flex flex-col min-h-[200px] snap-start hover:border-purple-300 hover:bg-gray-100/50 transition-all duration-200">
            {isAddingColumn ? (
              /* Add Column Form */
              <div className="space-y-3">
                <input
                  type="text"
                  placeholder="Enter list title..."
                  value={newColumnTitle}
                  onChange={(e) => setNewColumnTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleAddColumn();
                    } else if (e.key === 'Escape') {
                      handleCancelAddColumn();
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  autoFocus
                />
                <div className="flex space-x-2">
                  <button
                    onClick={handleAddColumn}
                    disabled={!newColumnTitle.trim()}
                    className="px-3 py-1 text-sm font-medium text-white bg-purple-700 hover:bg-purple-800 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 active:scale-95"
                  >
                    Add list
                  </button>
                  <button
                    onClick={handleCancelAddColumn}
                    className="px-3 py-1 text-sm font-medium text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              /* Add Column Button */
              <div className="flex items-center justify-center h-full">
                <button 
                  onClick={() => setIsAddingColumn(true)}
                  className="flex items-center text-gray-600 hover:text-gray-800 font-medium"
                >
                  <Plus size={20} className="mr-2" />
                  Add another list
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Column Settings Modal */}
      {showColumnSettings && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={handleCloseColumnSettings}
        >
          <div 
            className="bg-white rounded-2xl p-8 w-96 max-w-md mx-4 shadow-xl border border-gray-200/50"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">List Settings</h3>
            
            {/* Edit Title Section */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                List Title
              </label>
              {editingColumn === showColumnSettings ? (
                <div className="space-y-3">
                  <input
                    type="text"
                    value={editColumnTitle}
                    onChange={(e) => setEditColumnTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleUpdateColumnTitle(showColumnSettings, editColumnTitle);
                      } else if (e.key === 'Escape') {
                        setEditingColumn(null);
                        setEditColumnTitle('');
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="Enter list title..."
                    autoFocus
                  />
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleUpdateColumnTitle(showColumnSettings, editColumnTitle)}
                      disabled={!editColumnTitle.trim()}
                      className="px-3 py-1 text-sm font-medium text-white bg-purple-700 hover:bg-purple-800 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 active:scale-95"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => {
                        setEditingColumn(null);
                        setEditColumnTitle('');
                      }}
                      className="px-3 py-1 text-sm font-medium text-gray-600 hover:text-gray-800"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <span className="text-gray-900">
                    {boardData?.columns.find(col => col.id === showColumnSettings)?.title}
                  </span>
                  <button
                    onClick={() => {
                      const column = boardData?.columns.find(col => col.id === showColumnSettings);
                      if (column) {
                        handleStartEditingColumn(showColumnSettings, column.title);
                      }
                    }}
                    className="text-gray-500 hover:text-gray-700 p-1 rounded hover:bg-gray-100"
                  >
                    <Edit3 size={16} />
                  </button>
                </div>
              )}
            </div>



            {/* Actions */}
            <div className="flex justify-between">
              <button
                onClick={() => handleDeleteColumn(showColumnSettings)}
                className="flex items-center px-3 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md"
              >
                <Trash2 size={16} className="mr-2" />
                Delete List
              </button>
              
              <button
                onClick={handleCloseColumnSettings}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Card Settings Modal */}
      {showCardSettings && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={handleCloseCardSettings}
        >
          <div 
            className="bg-white rounded-2xl p-8 w-96 max-w-md mx-4 shadow-xl border border-gray-200/50"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Card Settings</h3>
            
            {/* Edit Title Section */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title
              </label>
              <input
                type="text"
                value={editCardTitle}
                onChange={(e) => setEditCardTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleUpdateCard(showCardSettings);
                  } else if (e.key === 'Escape') {
                    handleCloseCardSettings();
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                placeholder="Enter card title..."
              />
            </div>

            {/* Edit Description Section */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={editCardDescription}
                onChange={(e) => setEditCardDescription(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.ctrlKey) {
                    handleUpdateCard(showCardSettings);
                  } else if (e.key === 'Escape') {
                    handleCloseCardSettings();
                  }
                }}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
                placeholder="Enter card description..."
              />
            </div>

            {/* Schedule Section */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Schedule
              </label>
              <input
                type="datetime-local"
                value={editCardScheduledDate}
                onChange={(e) => handleScheduledDateChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                min={(() => {
                  const now = new Date();
                  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
                  return now.toISOString().slice(0, 16);
                })()} // Prevent selecting past dates using local timezone
              />
              <p className="text-xs text-gray-500 mt-1">
                Select when this task should be completed
              </p>
            </div>

            {/* Edit Status Section */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={editCardStatus}
                onChange={(e) => setEditCardStatus(e.target.value as TaskCard['status'])}
                disabled={!!editCardScheduledDate} // Disable when scheduled
                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                {editCardScheduledDate ? (
                  // Only show "Scheduled" when there's a scheduled date
                  <option value="scheduled">Scheduled</option>
                ) : (
                  // Show other options when no scheduled date
                  <>
                    <option value="in-progress">In Progress</option>
                    <option value="completed">Completed</option>
                  </>
                )}
              </select>
              {editCardScheduledDate && (
                <p className="text-xs text-gray-500 mt-1">
                  Status is automatically set to "Scheduled" when a date is selected
                </p>
              )}
            </div>

            {/* Edit Urgency Section */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Urgency
              </label>
              <select
                value={editCardUrgency}
                onChange={(e) => setEditCardUrgency(e.target.value as TaskCard['urgency'])}
                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="alert">Alert</option>
              </select>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleCloseCardSettings}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={() => handleUpdateCard(showCardSettings)}
                disabled={!editCardTitle.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-700 hover:bg-purple-800 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 active:scale-95"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}; 
